import React, { useState, useEffect, useCallback, memo, useMemo } from 'react';
import { User, Mail, Calendar, Settings, Save, Edit3, ArrowLeft, Key, X, Camera } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Componente memorizado para cards de estatísticas
const StatCard = memo(({ icon: Icon, title, description, color }) => (
  <motion.div 
    whileHover={{ scale: 1.02 }}
    className="bg-slate-900/50 backdrop-blur-sm rounded-xl border border-slate-800 p-6 text-center shadow-lg hover:shadow-cyan-500/5 transition-all duration-300"
  >
    <div className={`w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4 bg-${color}-500/10 text-${color}-400`}>
      <Icon size={24} />
    </div>
    <h3 className="text-lg font-semibold text-slate-200 mb-2">{title}</h3>
    <p className="text-slate-400 text-sm">{description}</p>
  </motion.div>
));

// Componente memorizado para modal de senha
const PasswordModal = memo(({ isOpen, onClose, onSubmit, loading }) => {
  const [passwords, setPasswords] = useState({ newPassword: '', confirmPassword: '' });
  const [error, setError] = useState('');

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setPasswords(prev => ({ ...prev, [name]: value }));
    setError(''); // Limpar erro ao digitar
  }, []);

  const handleSubmit = useCallback(() => {
    const { newPassword, confirmPassword } = passwords;
    
    if (!newPassword.trim()) {
      setError('Digite uma nova senha');
      return;
    }
    if (newPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }
    
    onSubmit(newPassword);
  }, [passwords, onSubmit]);

  const handleClose = useCallback(() => {
    setPasswords({ newPassword: '', confirmPassword: '' });
    setError('');
    onClose();
  }, [onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={handleClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl max-w-md w-full relative z-10 overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-100 flex items-center gap-2">
                  <Key size={24} className="text-cyan-400" />
                  Alterar Senha
                </h3>
                <button
                  onClick={handleClose}
                  className="text-slate-400 hover:text-slate-200 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Nova Senha
                  </label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwords.newPassword}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-lg text-slate-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none placeholder-slate-600"
                    placeholder="Digite sua nova senha"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Confirmar Senha
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwords.confirmPassword}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-lg text-slate-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none placeholder-slate-600"
                    placeholder="Confirme sua nova senha"
                    disabled={loading}
                  />
                </div>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                    <p className="text-sm text-red-400">{error}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleClose}
                  disabled={loading}
                  className="flex-1 px-4 py-3 border border-slate-700 text-slate-300 rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !passwords.newPassword || !passwords.confirmPassword}
                  className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-4 py-3 rounded-lg hover:from-cyan-500 hover:to-blue-500 transition-all shadow-lg shadow-cyan-500/20 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                >
                  {loading ? 'Alterando...' : 'Alterar Senha'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
});

const Profile = () => {
  const { user, updateProfile, updatePassword } = useAuth();
  const navigate = useNavigate();
  
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  
  // Dados do formulário memorizados
  const [formData, setFormData] = useState(() => ({
    full_name: '',
    email: '',
    phone: '',
    bio: '',
    photoURL: ''
  }));

  // Inicializar dados quando user mudar
  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.displayName || user.name || user.user_metadata?.full_name || user.email?.split('@')[0] || '',
        email: user.email || '',
        phone: user.user_metadata?.phone || '',
        bio: user.user_metadata?.bio || '',
        photoURL: user.photoURL || user.avatar || ''
      });
    }
  }, [user?.email, user?.displayName, user?.name, user?.photoURL, user?.avatar, user?.user_metadata?.full_name, user?.user_metadata?.phone, user?.user_metadata?.bio]);

  // Handlers otimizados
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleAvatarChange = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) return;
    if (file.size > 2 * 1024 * 1024) {
      window.alert('A foto deve ter no máximo 2 MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setFormData(prev => ({ ...prev, photoURL: String(reader.result || '') }));
    };
    reader.readAsDataURL(file);
  }, []);

  const handleSave = useCallback(async () => {
    setLoading(true);
    try {
      const result = await updateProfile({
        displayName: formData.full_name,
        photoURL: formData.photoURL
      });
      if (result?.success === false) {
        throw new Error(result.error || 'Erro ao atualizar perfil');
      }
      setIsEditing(false);
      // Usar um sistema de toast seria ideal aqui, por enquanto mantemos o alert mas poderíamos integrar depois
      // alert('Perfil atualizado com sucesso!'); 
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      // alert('Erro ao atualizar perfil. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [formData, updateProfile]);

  const handlePasswordUpdate = useCallback(async (newPassword) => {
    setLoading(true);
    try {
      const result = await updatePassword(newPassword);
      if (result.success) {
        // alert('Senha atualizada com sucesso!');
        setShowPasswordModal(false);
      } else {
        // alert(result.error || 'Erro ao atualizar senha');
      }
    } catch (error) {
      console.error('Erro ao atualizar senha:', error);
      // alert('Erro de conexão. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [updatePassword]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    if (user) {
      setFormData({
        full_name: user.displayName || user.name || user.user_metadata?.full_name || user.email?.split('@')[0] || '',
        email: user.email || '',
        phone: user.user_metadata?.phone || '',
        bio: user.user_metadata?.bio || '',
        photoURL: user.photoURL || user.avatar || ''
      });
    }
  }, [user]);

  // Dados formatados memorizados
  const formattedDate = useMemo(() => {
    return user?.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR') : 'Não informado';
  }, [user?.created_at]);

  const displayName = useMemo(() => {
    return formData.full_name || 'Usuário';
  }, [formData.full_name]);

  // Cards de estatísticas memorizados
  const statCards = useMemo(() => [
    {
      icon: Settings,
      title: 'Configurações',
      description: 'Gerencie suas preferências',
      color: 'blue'
    },
    {
      icon: User,
      title: 'Ativo',
      description: 'Conta verificada e ativa',
      color: 'green'
    },
    {
      icon: Calendar,
      title: 'Membro',
      description: `Desde ${formattedDate}`,
      color: 'purple'
    }
  ], [formattedDate]);

  return (
    <div className="min-h-screen bg-slate-950 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-slate-950 text-slate-200">
      {/* Header Simplificado */}
      <div className="bg-slate-900/50 backdrop-blur-xl border-b border-slate-800 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors text-slate-400 hover:text-cyan-400"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Meu Perfil</h1>
                <p className="text-slate-400 text-sm">Gerencie suas informações pessoais</p>
              </div>
            </div>
            
            {!isEditing ? (
              <div className="flex gap-2">
                <button
                  onClick={() => setIsEditing(true)}
                  className="bg-slate-800 hover:bg-slate-700 text-cyan-400 px-4 py-2 rounded-lg transition-all border border-slate-700 hover:border-cyan-500/50 flex items-center gap-2 text-sm font-medium"
                >
                  <Edit3 size={16} />
                  Editar
                </button>
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className="bg-slate-800 hover:bg-slate-700 text-cyan-400 px-4 py-2 rounded-lg transition-all border border-slate-700 hover:border-cyan-500/50 flex items-center gap-2 text-sm font-medium"
                >
                  <Key size={16} />
                  Senha
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 border border-slate-700 text-slate-400 rounded-lg hover:bg-slate-800 transition-colors text-sm font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-cyan-500 hover:to-blue-500 transition-all shadow-lg shadow-cyan-500/20 flex items-center gap-2 disabled:opacity-50 text-sm font-medium"
                >
                  <Save size={16} />
                  {loading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl mx-auto px-6 py-8"
      >
        <div className="bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-slate-800 overflow-hidden shadow-xl">
          {/* Header do Perfil */}
          <div className="relative h-48 bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 overflow-hidden">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1579546929518-9e396f3cc809?auto=format&fit=crop&q=80')] opacity-10 bg-cover bg-center" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" />
            
            <div className="absolute bottom-0 left-0 w-full px-8 py-6">
              <div className="flex items-end gap-6">
                <motion.div 
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="w-24 h-24 bg-slate-950 rounded-full p-1 border-4 border-slate-800 shadow-2xl relative z-10"
                >
                  <div className="w-full h-full bg-slate-800 rounded-full flex items-center justify-center overflow-hidden">
                    {formData.photoURL ? (
                      <img src={formData.photoURL} alt={displayName} className="w-full h-full object-cover" />
                    ) : (
                      <User size={40} className="text-cyan-400" />
                    )}
                  </div>
                  {isEditing && (
                    <label
                      className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 flex items-center justify-center cursor-pointer shadow-lg border-2 border-slate-900"
                      title="Alterar foto"
                    >
                      <Camera size={17} />
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleAvatarChange}
                      />
                    </label>
                  )}
                </motion.div>
                <div className="mb-2">
                  <motion.h2 
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="text-3xl font-bold text-white mb-1"
                  >
                    {displayName}
                  </motion.h2>
                  <motion.div 
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="flex items-center gap-4 text-slate-400 text-sm"
                  >
                    <p className="flex items-center gap-1.5">
                      <Mail size={14} className="text-cyan-500" />
                      {formData.email}
                    </p>
                    <p className="flex items-center gap-1.5">
                      <Calendar size={14} className="text-purple-500" />
                      Membro desde {formattedDate}
                    </p>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>

          {/* Formulário do Perfil */}
          <div className="p-8 pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Nome Completo */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">
                  Nome Completo
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-lg text-slate-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none placeholder-slate-600 transition-all"
                    placeholder="Digite seu nome completo"
                  />
                ) : (
                  <div className="px-4 py-3 bg-slate-950/50 border border-slate-800/50 rounded-lg text-slate-300">
                    {formData.full_name || 'Não informado'}
                  </div>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">
                  Email
                </label>
                <div className="px-4 py-3 bg-slate-950/30 border border-slate-800/30 rounded-lg text-slate-500 cursor-not-allowed">
                  {formData.email}
                  <span className="text-xs block mt-1 text-slate-600">Email não pode ser alterado</span>
                </div>
              </div>

              {/* Telefone */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">
                  Telefone
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-lg text-slate-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none placeholder-slate-600 transition-all"
                    placeholder="(11) 99999-9999"
                  />
                ) : (
                  <div className="px-4 py-3 bg-slate-950/50 border border-slate-800/50 rounded-lg text-slate-300">
                    {formData.phone || 'Não informado'}
                  </div>
                )}
              </div>

              {/* ID do Usuário */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">
                  ID do Usuário
                </label>
                <div className="px-4 py-3 bg-slate-950/30 border border-slate-800/30 rounded-lg text-slate-500 font-mono text-sm">
                  {user?.id != null ? String(user.id).slice(0, 8) : '—'}
                </div>
              </div>
            </div>

            {/* Bio */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-slate-400 mb-2">
                Biografia
              </label>
              {isEditing ? (
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-lg text-slate-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none resize-none placeholder-slate-600 transition-all"
                  placeholder="Conte um pouco sobre você..."
                />
              ) : (
                <div className="px-4 py-3 bg-slate-950/50 border border-slate-800/50 rounded-lg text-slate-300 min-h-[100px]">
                  {formData.bio || 'Nenhuma biografia adicionada ainda.'}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cards de Estatísticas */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {statCards.map((card, index) => (
            <StatCard
              key={index}
              icon={card.icon}
              title={card.title}
              description={card.description}
              color={card.color}
            />
          ))}
        </div>
      </motion.div>

      {/* Modal de Senha */}
      <PasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSubmit={handlePasswordUpdate}
        loading={loading}
      />
    </div>
  );
};

export default Profile;